import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from "react";

// シンプル可愛いデザインの共通UI（水色・丸ゴシック・ピル型）。
// 汎用UIは本来 @morse/ui に集約するが、本案件は独立配布のためここに最小限で持つ。

function cx(...parts: (string | false | undefined)[]): string {
  return parts.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
};
export function Button({ variant = "primary", className, ...props }: ButtonProps) {
  const base =
    "inline-flex items-center justify-center gap-1 rounded-pill px-5 py-2.5 text-sm font-bold transition disabled:opacity-50 disabled:cursor-not-allowed";
  const styles = {
    primary: "bg-brand text-white shadow-pill hover:bg-brand-dark",
    ghost: "bg-white text-brand shadow-pill hover:bg-sky-soft",
    danger: "bg-white text-rose-500 shadow-pill hover:bg-rose-50",
  } as const;
  return <button className={cx(base, styles[variant], className)} {...props} />;
}

export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx("rounded-soft bg-white p-6 shadow-soft", className)}>{children}</div>;
}

export function Field({
  label,
  error,
  children,
  hint,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-bold text-slate-600">{label}</span>
      {children}
      {hint && !error && <span className="block text-xs text-slate-400">{hint}</span>}
      {error && <span className="block text-xs font-bold text-rose-500">{error}</span>}
    </label>
  );
}

const inputBase =
  "w-full rounded-soft border border-sky-base bg-cream px-4 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={inputBase} {...props} />;
}
export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cx(inputBase, "min-h-[96px]")} {...props} />;
}
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={inputBase} {...props} />;
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    // 企業審査
    pending: { label: "審査待ち", cls: "bg-amber-100 text-amber-700" },
    approved: { label: "承認済み", cls: "bg-emerald-100 text-emerald-700" },
    rejected: { label: "却下", cls: "bg-rose-100 text-rose-600" },
    // 申込
    applied: { label: "申込中", cls: "bg-sky-light text-brand-dark" },
    cancelled: { label: "取消", cls: "bg-slate-100 text-slate-500" },
  };
  const s = map[status] ?? { label: status, cls: "bg-slate-100 text-slate-500" };
  return (
    <span className={cx("inline-block rounded-pill px-3 py-1 text-xs font-bold", s.cls)}>
      {s.label}
    </span>
  );
}

// ページ見出し
export function PageTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-2xl font-extrabold text-brand-dark">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
  );
}

// 装飾用の雲（背景）
export function Clouds() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 overflow-hidden"
    >
      <div className="cloud left-[8%] top-10 h-16 w-40" />
      <div className="cloud left-[40%] top-4 h-20 w-56" />
      <div className="cloud right-[10%] top-12 h-14 w-36" />
    </div>
  );
}
